import { Component, EventEmitter, Input, OnInit, Output, ElementRef, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DealsOffersService, Brand } from '../../../../../core/services/deals-offers.service';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'app-voucher-purchase-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './voucher-purchase-modal.html',
    styleUrl: './voucher-purchase-modal.scss'
})
export class VoucherPurchaseModalComponent implements OnInit, OnDestroy {
    @Input() brand!: Brand;
    @Input() mobileNumber: string = '';
    @Output() close = new EventEmitter<void>();

    purchaseForm!: FormGroup;
    step: number = 0; // 0: Details View, 1: Form, 2: Success
    isLoading: boolean = false;
    showTerms: boolean = false;
    showRedeemSteps: boolean = true; // Expanded by default
    redeemStepsArray: Array<{ text: string, image: string }> = [];
    isCheckingStock: boolean = false;
    errorMessage: string | null = null;
    stockError: string | null = null;

    purchasedVouchers: any[] = [];
    successMessage: string = '';
    externalOrderId: string = '';
    productName: string = '';

    denominations: string[] = [];
    availableStock: number = 0;

    constructor(
        private fb: FormBuilder,
        private dealsService: DealsOffersService,
        private el: ElementRef,
        private cdr: ChangeDetectorRef
    ) {
        this.initForm();
    }

    ngOnInit(): void {
        if (!this.brand) {
            this.errorMessage = "Brand details missing.";
            return;
        }

        this.parseDenominations();
        this.parseStock();
        this.parseRedeemSteps();
        /*removed init form initialization*/
        // Move to body to escape widget container items if necessary (like z-index issues)
        document.body.appendChild(this.el.nativeElement);
    }

    ngOnDestroy(): void {
        if (this.el.nativeElement.parentNode === document.body) {
            document.body.removeChild(this.el.nativeElement);
        }
    }

    private parseDenominations(): void {
        if (this.brand.denominationList) {
            this.denominations = this.brand.denominationList.split(',').map(d => d.trim()).filter(d => d);
        }
    }

    private parseStock(): void {
        if (this.brand.stockAvailable) {
            const stock = parseInt(this.brand.stockAvailable, 10);
            if (!isNaN(stock)) this.availableStock = stock;
        }
    }

    private parseRedeemSteps(): void {
        if (this.brand.redeemSteps) {
            try {
                const steps = JSON.parse(this.brand.redeemSteps);
                this.redeemStepsArray = Object.keys(steps).map(key => ({
                    text: steps[key].text || '',
                    image: steps[key].image || ''
                }));
            } catch (e) {
                console.error('Error parsing redeem steps:', e);
                this.redeemStepsArray = [];
            }
        }
    }

    private initForm(): void {
        this.purchaseForm = this.fb.group({
            denomination: ['', Validators.required],
            quantity: [{ value: 1, disabled: true }, [Validators.required, Validators.min(1)]]
        });

        // Listen for denomination changes
        this.purchaseForm.get('denomination')?.valueChanges.subscribe(val => {
            if (val) {
                this.checkStockAvailability(val);
            }
        });
    }

    private checkStockAvailability(denomination: string): void {
        this.isCheckingStock = true;
        this.stockError = null;
        this.availableStock = 0;
        this.purchaseForm.get('quantity')?.disable();
        this.purchaseForm.patchValue({ quantity: 1 });

        this.dealsService.checkStock(this.brand.BrandProductCode, denomination)
            .pipe(finalize(() => {
                this.isCheckingStock = false;
                this.cdr.detectChanges();
            }))
            .subscribe({
                next: (res: any) => {
                    if (res && typeof res.AvailableQuantity === 'number') {
                        this.availableStock = res.AvailableQuantity;
                        if (this.availableStock > 0) {
                            this.purchaseForm.get('quantity')?.enable();
                            this.purchaseForm.get('quantity')?.setValidators([
                                Validators.required,
                                Validators.min(1),
                                Validators.max(this.availableStock)
                            ]);
                            this.purchaseForm.get('quantity')?.updateValueAndValidity();
                        } else {
                            this.stockError = 'Out of Stock';
                        }
                    } else {
                        // Fallback or error handling if structure differs
                        this.stockError = 'Unable to verify stock';
                    }
                },
                error: (err) => {
                    console.error('Stock check failed', err);
                    this.stockError = 'Error checking stock';
                }
            });
    }

    get totalCost(): number {
        const denom = parseFloat(this.purchaseForm.get('denomination')?.value || '0');
        const qty = this.purchaseForm.get('quantity')?.value || 0;
        return denom * qty;
    }

    submit(): void {
        if (this.purchaseForm.invalid) {
            this.purchaseForm.markAllAsTouched();
            return;
        }

        this.isLoading = true;
        this.errorMessage = null;

        const { denomination, quantity } = this.purchaseForm.value;

        // Generate Unique External Order ID
        this.externalOrderId = 'ORD-' + Date.now() + '-' + Math.floor(Math.random() * 1000);

        this.dealsService.pullVoucher(
            this.brand.BrandProductCode,
            denomination,
            quantity,
            this.externalOrderId
        ).pipe(
            finalize(() => {
                this.isLoading = false;
                this.cdr.detectChanges();
            })
        ).subscribe({
            next: (response: any) => {
                // Check for success logic based on API response structure provided
                // Response example: {"PullVouchers": [...], "ErrorCode": "", "ResultType": "SUCCESS", ...}

                if (response && response.ResultType === 'SUCCESS' && response.PullVouchers && response.PullVouchers.length > 0) {
                    const firstBatch = response.PullVouchers[0];
                    if (firstBatch && firstBatch.Vouchers) {
                        this.purchasedVouchers = firstBatch.Vouchers;
                        this.productName = firstBatch.ProductName || this.brand.BrandName;
                        this.successMessage = response.Message || 'Process successfully completed';
                        this.step = 2; // Move to Success Screen
                    } else {
                        this.errorMessage = "Vouchers not found in response.";
                    }
                } else {
                    this.errorMessage = response.ErrorMessage || response.Message || 'Transaction failed.';
                }
            },
            error: (err) => {
                console.error('Purchase Error:', err);
                this.errorMessage = 'Failed to purchase voucher. Please try again.';
            }
        });
    }

    closeModal(): void {
        if (!this.isLoading) {
            this.close.emit();
        }
    }

    copyToClipboard(text: string): void {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            // Optional: Show toast or tooltip (omitted for simplicity)
        });
    }

    toggleTerms(): void {
        this.showTerms = !this.showTerms;
    }

    toggleRedeemSteps(): void {
        this.showRedeemSteps = !this.showRedeemSteps;
    }

    proceedToPurchase(): void {
        // If OnlineRedemptionUrl exists, redirect to external website
        if (this.brand.OnlineRedemptionUrl && this.brand.OnlineRedemptionUrl.trim() !== '') {
            let url = this.brand.OnlineRedemptionUrl.trim();
            // Add https:// if no protocol is specified
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                url = 'https://' + url;
            }
            window.open(url, '_blank');
            this.closeModal();
        } else {
            // Otherwise, proceed to purchase form
            this.step = 1;
            this.cdr.detectChanges();
        }
    }

    hasRedemptionUrl(): boolean {
        return !!(this.brand.OnlineRedemptionUrl && this.brand.OnlineRedemptionUrl.trim() !== '');
    }
}
